import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Zap, CheckCircle, AlertCircle, Download } from 'lucide-react';
import './ExtractData.css';

function ExtractData() {
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadExtractions();
  }, []);

  const loadExtractions = async () => {
    try {
      const response = await axios.get('/api/outputs');
      setOutputs(response.data || []);
    } catch (err) {
      console.error('Failed to load extractions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (outputId, status) => {
    try {
      await axios.put(`/api/outputs/${outputId}/verify`, {
        status: status,
        notes: status === 'verified' ? 'Verified by user' : 'Rejected by user'
      });
      loadExtractions();
    } catch (err) {
      console.error('Failed to verify:', err);
      alert('Error updating status');
    }
  };

  const handleDownload = async (outputId) => {
    try {
      const response = await axios.get(`/api/outputs/${outputId}/json`);
      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/json;charset=utf-8,' +
        encodeURIComponent(JSON.stringify(response.data, null, 2)));
      element.setAttribute('download', `extraction-${outputId}.json`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (err) {
      console.error('Failed to download:', err);
      alert('Error downloading extraction');
    }
  };

  const filteredOutputs = outputs.filter(output => {
    if (filter === 'all') return true;
    return output.status === filter;
  });

  return (
    <div className="extract-data">
      <div className="page-header">
        <div>
          <h1>Extract Data</h1>
          <p>Review and verify extracted form data</p>
        </div>
        <div className="filters">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Extractions</option>
            <option value="pending">Pending Review</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <Zap size={48} />
          <p>Loading extractions...</p>
        </div>
      ) : filteredOutputs.length === 0 ? (
        <div className="empty-state">
          <Zap size={48} />
          <p>No extractions found</p>
          <small>Upload a PDF and process it to see extractions here</small>
        </div>
      ) : (
        <div className="extractions-list">
          {filteredOutputs.map(output => (
            <div key={output._id} className="extraction-card">
              <div className="extraction-header">
                <div className="extraction-info">
                  <h3>Extraction #{output._id?.toString().slice(-6)}</h3>
                  <p>Processed: {new Date(output.processingDetails?.processedAt).toLocaleString()}</p>
                </div>
                <span className={`status-badge ${output.status}`}>
                  {output.status}
                </span>
              </div>

              <div className="extraction-content">
                <div className="output-preview">
                  <h4>Extracted Fields</h4>
                  <pre>{JSON.stringify(output.output, null, 2)}</pre>
                </div>

                <div className="extraction-meta">
                  <div className="meta-item">
                    <span className="label">Confidence</span>
                    <span className="value">{output.processingDetails?.confidence || 0}%</span>
                  </div>
                  <div className="meta-item">
                    <span className="label">Processing Time</span>
                    <span className="value">{output.processingDetails?.processingTime || 0}ms</span>
                  </div>
                </div>
              </div>

              <div className="extraction-actions">
                {output.status === 'pending' && (
                  <>
                    <button
                      className="btn-success"
                      onClick={() => handleVerify(output._id, 'verified')}
                    >
                      <CheckCircle size={18} />
                      Verify
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => handleVerify(output._id, 'rejected')}
                    >
                      <AlertCircle size={18} />
                      Reject
                    </button>
                  </>
                )}
                <button
                  className="btn-secondary"
                  onClick={() => handleDownload(output._id)}
                >
                  <Download size={18} />
                  Download JSON
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ExtractData;
